import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HiOutlineCheck, HiOutlineExclamationCircle } from "react-icons/hi";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import {
  getLegalCertificateInformClientStatusPreview,
  getLegalCertificateProgress,
  LegalCertificateWorkflowStep,
  sendLegalCertificateInformClientStatus,
  type LegalCertificateInformClientApplicationStatus,
} from "../../api/legalCertificate";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";

const LABEL_FEATURES = { fontFeatureSettings: "'ss01' on, 'cv01' on" } as const;

export const LEGAL_CERTIFICATE_INFORM_CLIENT_FORM_ID =
  "legal-certificate-inform-client-form";

const PREREQUISITE_HINT =
  "Upload the scanned Legal Certificate (Status > Legal Certificate Received) before sending.";

type LegalCertificateInformClientViewProps = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function LegalCertificateInformClientView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: LegalCertificateInformClientViewProps) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [messagePreview, setMessagePreview] = useState("");
  const [applicationNumbers, setApplicationNumbers] = useState("");
  const [applications, setApplications] = useState<
    LegalCertificateInformClientApplicationStatus[]
  >([]);
  const [allLcUploaded, setAllLcUploaded] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasSentWhatsapp, setHasSentWhatsapp] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  useEffect(() => {
    onSentStatusChange?.(hasSentWhatsapp);
  }, [hasSentWhatsapp, onSentStatusChange]);

  useEffect(() => {
    if (!leadId) {
      setHasSentWhatsapp(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const progress = await getLegalCertificateProgress(leadId);
        const step = progress.steps.find(
          (s) =>
            s.slug ===
            LegalCertificateWorkflowStep.LC_S3_05_InformClientOfLegalCertificate
        );
        if (!cancelled && step) {
          setHasSentWhatsapp(step.isCompleted);
        }
      } catch {
        if (!cancelled) setHasSentWhatsapp(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  useEffect(() => {
    if (!leadId) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getLegalCertificateInformClientStatusPreview(leadId)
      .then((res) => {
        if (cancelled) return;
        setMessagePreview(res.message ?? "");
        setApplicationNumbers(res.applicationNumbers ?? "");
        setPhoneNumber(res.recipientPhone ?? "");
        setApplications(res.applications ?? []);
        setAllLcUploaded(res.allLegalCertificatesUploaded ?? false);
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err instanceof Error
            ? err.message
            : "Failed to load message preview";
        setPreviewError(msg);
        setMessagePreview("");
        setApplicationNumbers("");
        setApplications([]);
        setAllLcUploaded(false);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  const canSend =
    !previewError &&
    applicationNumbers.trim().length > 0 &&
    messagePreview.trim().length > 0 &&
    allLcUploaded;

  const pendingApplications = applications.filter(
    (app) => !app.isLegalCertificateUploaded
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || sending) return;
    if (hasSentWhatsapp) {
      onSuccess?.(leadId);
      return;
    }
    if (!canSend) {
      const pendingMsg =
        pendingApplications.length > 0
          ? `Upload the Legal Certificate for: ${pendingApplications
              .map((a) => a.tmNo ?? "—")
              .join(", ")}.`
          : null;
      toast.error(previewError ?? pendingMsg ?? PREREQUISITE_HINT);
      return;
    }
    setConfirmMode("send");
    setShowConfirmModal(true);
  };

  const handleConfirmProceed = () => {
    if (!leadId) return;
    setShowConfirmModal(false);
    setSending(true);
    (async () => {
      try {
        await sendLegalCertificateInformClientStatus(leadId);
        setHasSentWhatsapp(true);
        toast.success(
          confirmMode === "resend"
            ? "Message resent to client successfully."
            : "Message sent to client successfully."
        );
        onSuccess?.(leadId);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setSending(false);
      }
    })();
  };

  const handleResend = () => {
    if (!leadId || sending) return;
    if (!canSend) {
      const pendingMsg =
        pendingApplications.length > 0
          ? `Upload the Legal Certificate for: ${pendingApplications
              .map((a) => a.tmNo ?? "—")
              .join(", ")}.`
          : null;
      toast.error(previewError ?? pendingMsg ?? PREREQUISITE_HINT);
      return;
    }
    setConfirmMode("resend");
    setShowConfirmModal(true);
  };

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px] justify-center py-12"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps (select or create a lead) to access
          this step.
        </p>
      </div>
    );
  }

  return (
    <form
      id={LEGAL_CERTIFICATE_INFORM_CLIENT_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-[22px] w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="flex flex-col gap-5">
        <p className="text-[16px] leading-5 text-[#222]">Message Preview</p>
        <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px] min-h-[148px]">
          {previewLoading && !messagePreview ? (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              Loading preview...
            </p>
          ) : messagePreview ? (
            <pre
              className="font-sans text-[12px] leading-5 text-[#222] m-0 whitespace-pre-wrap break-words"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              {renderWhatsAppFormatting(messagePreview)}
            </pre>
          ) : (
            <p
              className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0"
              style={{ fontFeatureSettings: "'ss01' on, 'cv01' on" }}
            >
              {previewError ?? PREREQUISITE_HINT}
            </p>
          )}
        </div>
      </div>

      {/* Applications — one row per TM No. with Legal Certificate upload status */}
      <div className="flex flex-col gap-2">
        <p className="text-xs font-normal text-[#222]" style={LABEL_FEATURES}>
          Application(s) ({applications.length || 0})
        </p>
        {previewLoading && applications.length === 0 ? (
          <p className="text-xs text-[#6D6D6D]" style={LABEL_FEATURES}>
            Loading applications…
          </p>
        ) : applications.length === 0 ? (
          <p className="text-xs text-[#6D6D6D]" style={LABEL_FEATURES}>
            {previewError ??
              "No application numbers found. Complete Application Details first."}
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {applications.map((app) => (
              <div
                key={app.applicationId}
                className="flex flex-row items-center gap-2.5 px-[10px] py-2 min-h-9 bg-[#F7F7F7] border border-[#EEEEEE] rounded-[6px]"
              >
                <span
                  className="text-[12px] font-medium leading-5 text-[#222222]"
                  style={LABEL_FEATURES}
                >
                  TM No. {app.tmNo ?? "—"}
                </span>
                {app.mark ? (
                  <span
                    className="text-[12px] font-normal leading-5 text-[#6D6D6D] truncate flex-1"
                    style={LABEL_FEATURES}
                    title={app.mark}
                  >
                    · {app.mark}
                  </span>
                ) : (
                  <span className="flex-1" />
                )}
                {app.isLegalCertificateUploaded ? (
                  <span
                    className="flex items-center gap-1 text-[11px] font-medium text-[#16A34A] flex-shrink-0"
                    style={LABEL_FEATURES}
                  >
                    <HiOutlineCheck className="w-3.5 h-3.5" strokeWidth={2.5} />
                    Certificate received
                  </span>
                ) : (
                  <span
                    className="flex items-center gap-1 text-[11px] font-medium text-[#B45309] flex-shrink-0"
                    style={LABEL_FEATURES}
                  >
                    <HiOutlineExclamationCircle className="w-3.5 h-3.5" />
                    Pending upload
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
        {!previewLoading && applications.length > 0 && !allLcUploaded ? (
          <p className="text-[11px] text-[#B45309]" style={LABEL_FEATURES}>
            Upload the Legal Certificate for every application in Step 1 before
            sending this message.
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {hasSentWhatsapp && (
          <button
            type="button"
            disabled={sending || !canSend}
            onClick={handleResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Resend Message"}
          </button>
        )}
      </div>

      <ConfirmMessageModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        previewContent={messagePreview}
        onConfirm={handleConfirmProceed}
        disableConfirm={!canSend}
      />
    </form>
  );
}
